/** Inline artifact card for ChatBox `data-artifact` parts: a compact card that opens the artifact panel. The
 * panel is the render surface — the card only advertises the artifact (icon by kind + name + download). Unknown
 * kinds get a generic icon and still open (the panel's fallback renderer handles them); intermediate artifacts
 * surface only in the turn's "Working files" disclosure. */

import { Article, BarChart, Code, FileDownloadOutlined, InsertDriveFileOutlined, Notes, TableChart } from "@mui/icons-material"
import type { SvgIconComponent } from "@mui/icons-material"
import { Box, ButtonBase, IconButton, Typography, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useMemo } from "react"
import type { MouseEvent } from "react"
import type { ChatDataMessagePart, ChatMessage } from "@mui/x-chat-headless"
import type { ChatPartRendererProps } from "@mui/x-chat-headless"
import { fileDownload } from "helpers/csv"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import {
  type ArtifactEnvelope,
  type FileArtifact,
  type KnownArtifact,
  isArtifactEnvelope,
  artifactKindLabel,
  isFileArtifact,
  normalizeFileArtifact,
} from "./artifact-contract"
import { isIntermediateArtifact } from "./working-files"
import { useArtifactPanelActions } from "./use-artifact-panel"
import { useArtifactDownload } from "./use-artifact-download"
import { artifactCardName } from "./artifact-card-name"
import { artifactDownloadFilename, artifactDownloadPayload } from "./artifact-download"

// Open lookup; the `satisfies` check keeps the known kinds exhaustive so a new kind can't ship silently iconless.
const ARTIFACT_ICONS: Partial<Record<string, SvgIconComponent>> = {
  table: TableChart,
  plot: BarChart,
  text: Article,
  markdown: Notes,
  html: Code,
  file: InsertDriveFileOutlined,
} satisfies Record<KnownArtifact["type"], SvgIconComponent>

// Scheme-aware CSS vars, not theme.palette.* (cssVariables freezes those to light — invisible border + broken hover in dark).
const cardShellStyle = (theme: Theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  width: "100%",
  maxWidth: 360,
  // Centered in the conversation column, with breathing room above so the first card doesn't hug the paragraph before it.
  marginInline: "auto",
  marginBlock: theme.spacing(2, 0.5),
  border: "1px solid var(--app-overlay-border)",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  "&:hover": { backgroundColor: "var(--app-subtle-hover-surface)" },
})

const IconTile = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: theme.spacing(4),
  height: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "var(--mui-palette-primary-main)",
  color: "var(--mui-palette-primary-contrastText)",
}))

const TEXT_SX = { flex: 1, minWidth: 0 } satisfies SxProps<Theme>
const LABEL_SX = { fontWeight: 600 } satisfies SxProps<Theme>

const FileCardRoot = styled(Box)(({ theme }) => cardShellStyle(theme))

const FileCardBody = styled(ButtonBase)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
  textAlign: "left",
  borderRadius: theme.shape.borderRadius,
}))

function FileArtifactCard({ artifact }: { artifact: FileArtifact }) {
  const actions = useArtifactPanelActions()
  const activeThreadId = actions?.activeThreadId ?? null
  const { downloadArtifact, isDownloading } = useArtifactDownload(artifact, activeThreadId)
  const { filename } = normalizeFileArtifact(artifact)
  const caption = `${artifactKindLabel(artifact)} · click to open`

  const onOpen = (event: MouseEvent<HTMLButtonElement>) => actions?.openArtifact(artifact, event.currentTarget, filename)
  const onDownload = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (activeThreadId == null) return
    downloadArtifact(activeThreadId, artifact.id)
  }

  return (
    <FileCardRoot data-testid="chatbox-artifact-part" data-artifact-type={artifact.type}>
      <FileCardBody onClick={onOpen} aria-label={`Open artifact: ${filename}`}>
        <IconTile><InsertDriveFileOutlined fontSize="small" /></IconTile>
        <Box sx={TEXT_SX}>
          <Typography variant="body2" noWrap sx={LABEL_SX}>{filename}</Typography>
          <Typography variant="caption" color="text.secondary">{caption}</Typography>
        </Box>
      </FileCardBody>
      <IconButton
        size="small"
        aria-label={`Download ${filename}`}
        data-testid="chatbox-artifact-part-download"
        disabled={activeThreadId == null || isDownloading}
        onClick={onDownload}
      >
        <FileDownloadOutlined fontSize="small" />
      </IconButton>
    </FileCardRoot>
  )
}

function ArtifactCard({ artifact, message }: { artifact: ArtifactEnvelope, message: ChatMessage }) {
  const actions = useArtifactPanelActions()
  const activeThreadId = actions?.activeThreadId ?? null
  const analytics = useAnalytics()
  const Icon = ARTIFACT_ICONS[artifact.type] ?? InsertDriveFileOutlined
  const name = artifactCardName(artifact, message)
  const caption = `${artifactKindLabel(artifact)} · click to open`
  const payload = useMemo(() => artifactDownloadPayload(artifact), [artifact])

  const onOpen = (event: MouseEvent<HTMLButtonElement>) => actions?.openArtifact(artifact, event.currentTarget, name)
  const onDownload = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (payload === null) return
    fileDownload(payload.data, artifactDownloadFilename(name, payload.extension), payload.mimeType)
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_DOWNLOADED, {
      artifactId: artifact.id,
      artifactType: artifact.type,
      mediaType: payload.mimeType,
      extension: payload.extension,
      ...(activeThreadId != null && { threadId: activeThreadId }),
    })
  }

  return (
    <FileCardRoot data-testid="chatbox-artifact-part" data-artifact-type={artifact.type}>
      <FileCardBody onClick={onOpen} aria-label={`Open artifact: ${name}`}>
        <IconTile><Icon fontSize="small" /></IconTile>
        <Box sx={TEXT_SX}>
          <Typography variant="body2" noWrap sx={LABEL_SX}>{name}</Typography>
          <Typography variant="caption" color="text.secondary">{caption}</Typography>
        </Box>
      </FileCardBody>
      <IconButton
        size="small"
        aria-label={`Download ${name}`}
        data-testid="chatbox-artifact-part-download"
        disabled={payload === null}
        onClick={onDownload}
      >
        <FileDownloadOutlined fontSize="small" />
      </IconButton>
    </FileCardRoot>
  )
}

export function ArtifactPart({ part, message }: ChatPartRendererProps<ChatDataMessagePart>) {
  const { data } = part
  if (!isArtifactEnvelope(data)) return null
  // Intermediate artifacts surface only in the turn's "Working files" disclosure, not as an inline card.
  if (isIntermediateArtifact(data)) return null
  if (isFileArtifact(data)) return <FileArtifactCard artifact={data} />
  return <ArtifactCard artifact={data} message={message} />
}
